import { type KeyboardEvent as ReactKeyboardEvent, type ReactNode, useEffect, useId, useRef } from "react"
import { useNarrow } from "@/lib/use-media-query"
import { cn } from "@/lib/utils"
import { Sheet } from "./sheet"

/** The rows a menu moves between with ↑/↓ (plain items, and the radio items of a filter or a scope picker). */
const MENU_ITEMS = "[role=menuitem],[role=menuitemradio]"
/** What a dialog popover focuses first: its first field (document order), else its first button. */
const FIELDS = "input:not([type=hidden]),textarea,select"

/**
 * A menu that opens under its trigger (desktop) or as a bottom sheet (< 768px). Closes on Escape, on a click outside
 * and when an item is chosen (callers close it in their handlers). ↑/↓ move between menu items. `role="dialog"` is for
 * a small form (Capture): no menu semantics, and its first field takes focus when it opens.
 */
export function Popover({
  open,
  onOpenChange,
  trigger,
  title,
  align = "start",
  role = "menu",
  className,
  children,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Renders the trigger; spread the props onto the button. */
  trigger: (props: {
    readonly "aria-haspopup": "menu" | "dialog"
    readonly "aria-expanded": boolean
    readonly "aria-controls": string
    readonly onClick: () => void
  }) => ReactNode
  /** `menu` (default): rows of `menuitem`s; `dialog`: a form. */
  role?: "menu" | "dialog"
  /** Sheet title on phones. */
  title: string
  align?: "start" | "end"
  className?: string
  children: ReactNode
}) {
  const narrow = useNarrow()
  const id = useId()
  const root = useRef<HTMLDivElement>(null)
  const panel = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open || narrow) return
    const onDown = (event: MouseEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) onOpenChange(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onOpenChange(false)
    }
    document.addEventListener("mousedown", onDown)
    document.addEventListener("keydown", onKey)
    // Focus the first item (a menu) or the first field (a dialog) so the keyboard works at once.
    const first =
      role === "dialog"
        ? (panel.current?.querySelector<HTMLElement>(FIELDS) ??
          panel.current?.querySelector<HTMLElement>("button"))
        : panel.current?.querySelector<HTMLElement>(MENU_ITEMS)
    first?.focus()
    return () => {
      document.removeEventListener("mousedown", onDown)
      document.removeEventListener("keydown", onKey)
    }
  }, [open, narrow, onOpenChange, role])

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (role !== "menu" || (event.key !== "ArrowDown" && event.key !== "ArrowUp")) return
    const items = [...(panel.current?.querySelectorAll<HTMLElement>(MENU_ITEMS) ?? [])]
    if (items.length === 0) return
    event.preventDefault()
    const at = items.indexOf(document.activeElement as HTMLElement)
    const next = event.key === "ArrowDown" ? (at + 1) % items.length : (at - 1 + items.length) % items.length
    items[next]?.focus()
  }

  // A menu (rows of menu items, ↑/↓), or a form: a dialog, or in a phone sheet plain content (the sheet itself is the
  // native dialog, titled). Spelled out per role so each element's ARIA is checked.
  const body =
    role === "menu" ? (
      <div ref={panel} id={id} role="menu" aria-label={title} onKeyDown={onKeyDown} className="flex flex-col">
        {children}
      </div>
    ) : narrow ? (
      // the sheet carries the title
      <div ref={panel} id={id} className="flex flex-col">
        {children}
      </div>
    ) : (
      <div ref={panel} id={id} role="dialog" aria-label={title} className="flex flex-col">
        {children}
      </div>
    )

  return (
    // min-w-0: a trigger in a crowded flex row (the phone header's workspace picker) truncates instead of overflowing
    <div ref={root} className="relative min-w-0">
      {trigger({
        "aria-haspopup": role,
        "aria-expanded": open,
        "aria-controls": id,
        onClick: () => onOpenChange(!open),
      })}
      {narrow
        ? // decision 0151: the sheet mounts only while open (a phone's chat mounted one <dialog> per message's ⋯ menu)
          open && (
            <Sheet open={open} onOpenChange={onOpenChange} title={title} side="bottom">
              <div className="px-3 pt-3 pb-6">{body}</div>
            </Sheet>
          )
        : open && (
            <div
              className={cn(
                "absolute top-full z-30 mt-1.5 w-72 rounded-sheet border border-rule bg-surface p-1.5 shadow-sheet",
                align === "end" ? "right-0" : "left-0",
                className,
              )}
            >
              {body}
            </div>
          )}
    </div>
  )
}

/** One row of a Popover menu (a link or button styled the same; pass `role="menuitem"` through). */
export const menuItemClass =
  // keyboard focus is the global ink ring, drawn inside the row (a fill alone is too faint on white)
  "flex min-h-8 w-full items-center gap-2.5 rounded-control px-2 py-1.5 text-left text-[14px] text-ink no-underline hover:bg-fill hover:text-ink focus-visible:bg-fill focus-visible:-outline-offset-2 max-md:min-h-11"

export const MenuSeparator = () => <hr className="my-1 border-0 border-t border-rule" />
